// Single normalizer for SAT fiscal regimes used by segmentation.
//
// The same regime is spelled a dozen ways across the codebase and the backend:
// SAT numeric codes ('625'), declaration types ('platec'), enum aliases ('pt'),
// onboarding answers ('plataformas'). This module collapses all of them into one
// closed set so nothing downstream has to know which spelling it received.
//
// Guarantee: `normalizeRegime` returns the canonical physical-person regime for
// any recognized spelling, or `null` for anything else (empty, onboarding
// sentinels like 'no_se', or codes with no dedicated segment such as the moral
// regimes). It never throws.

export type CanonicalRegime =
  | 'resico' // 626
  | 'plataformas' // 625 — plataformas tecnológicas (PLATEC)
  | 'empresarial' // 612 — actividad empresarial y profesional
  | 'rif' // 621
  | 'sueldos' // 605 — sueldos y salarios
  | 'arrendamiento'; // 606

const REGIME_ALIASES: Record<string, CanonicalRegime> = {
  '626': 'resico',
  resico: 'resico',

  '625': 'plataformas',
  plataformas: 'plataformas',
  platec: 'plataformas',
  pt: 'plataformas',

  '612': 'empresarial',
  empresarial: 'empresarial',
  business: 'empresarial',
  act_emp: 'empresarial',

  '621': 'rif',
  rif: 'rif',

  '605': 'sueldos',
  sueldos: 'sueldos',
  salarios: 'sueldos',
  sys: 'sueldos',

  '606': 'arrendamiento',
  arrendamiento: 'arrendamiento',
  lease: 'arrendamiento',
  arriendos: 'arrendamiento',
};

export const CANONICAL_REGIME_LABELS: Record<CanonicalRegime, string> = {
  resico: 'RESICO',
  plataformas: 'Plataformas tecnológicas',
  empresarial: 'Actividad empresarial',
  rif: 'RIF',
  sueldos: 'Sueldos y salarios',
  arrendamiento: 'Arrendamiento',
};

// Onboarding régimen questions offer non-régime escape answers: "no sé", "aún no
// me doy de alta", "otro régimen". They are legitimate choices but NOT régimenes,
// so `normalizeRegime` returns null for them and `resolveDeclaredRegimes` filters
// them out. Exported so `validateFlow` can tell a permitted sentinel apart from a
// typo when it checks a `declaresRegimes` step's choice values.
export const NON_REGIME_SENTINELS: ReadonlySet<string> = new Set([
  'no_se',
  'no_alta',
  'otro',
]);

export function normalizeRegime(input: string | null | undefined): CanonicalRegime | null {
  if (!input) return null;
  return REGIME_ALIASES[input.trim().toLowerCase()] ?? null;
}

export function normalizeRegimes(inputs: readonly (string | null | undefined)[] | null | undefined): CanonicalRegime[] {
  if (!inputs) return [];
  const seen = new Set<CanonicalRegime>();
  for (const raw of inputs) {
    const regime = normalizeRegime(raw);
    if (regime) seen.add(regime);
  }
  return [...seen];
}
